import React from 'react';

import { JsonParam, useQueryParam, withDefault } from 'use-query-params';
import PropTypes from 'prop-types';

import Pagination from 'modules/common/components/Pagination';
import GenericTable from 'modules/common/components/GenericTable';
import { Button } from 'modules/common/input';
import EmptyState from 'modules/common/components/EmptyState';
import { showDrawer } from 'modules/common/drawer/DrawerMarker';
import PrintTemplate from 'modules/branch/services-management/services/components/payment-methods/PrintTemplate';
import { formatCurrency, print } from 'modules/common/utils';

import {
  useGetRecentTransactions,
  useGetSelectedRecentTransaction,
} from '../hooks';
import { DEFAULT_FILTER } from '../components/Filter';
import OrderDetailsModal from '../modals/OrderDetailsModal';
import { useGetCart } from '../../../services/hooks';

function RecentTransactions({ canProceed, branch }) {
  const [, , includes] = useGetCart();
  const [, data, refresh] = useGetSelectedRecentTransaction(null, null, {
    disableAction: true,
  });

  const [query, setQuery] = useQueryParam(
    'recent_transactions',
    withDefault(JsonParam, DEFAULT_FILTER)
  );

  const [isListLoading, list, pagination] = useGetRecentTransactions({
    params: { ...query, branch_uuid: branch?.value },
  });

  const hasSelectedBranch = canProceed(true, {
    withToast: false,
  })();

  const handleOnChangePage = (page) => {
    setQuery({
      ...query,
      page,
    });
  };

  const handleOnView = (row) => () => {
    showDrawer({
      subTitleClassName: 'hidden',
      content: (onClose) => (
        <OrderDetailsModal
          onClose={onClose}
          data={row}
          includes={includes}
          onRefresh={() => {
            refresh(
              {
                order_uuid: row?.uuid,
              },
              () => print('print-template')
            );
            onClose();
          }}
        />
      ),
      align: 'left',
    });
  };

  return (
    <div className="flex flex-col flex-1 p-3 lg:px-10 lg:p-5 overflow-y-auto overflow-x-hidden">
      <div className="hidden" id="print-template">
        <PrintTemplate
          businessName={branch?.business_name}
          branchName={branch?.branch_name}
          businessLogo={branch?.logo_url}
          address={branch?.address_details || branch?.address?.address_details}
          cart={data?.cart}
          includes={includes}
          paymentMethod={data?.payment_method}
          amountTendered={data?.amount_tendered}
          change={data?.change}
          data={{
            subtotal: formatCurrency(data?.subtotal || 0),
            discount: formatCurrency(data?.total_discount || 0),
            totalVAT: data?.tax,
            VAT: data?.tax,
            total: formatCurrency(data?.total || 0),
          }}
        />
      </div>

      <div className="flex flex-col flex-1 card p-0 md:p-5 border-0 md:border space-y-3">
        {hasSelectedBranch && (
          <div>
            <div className="font-semibold">Recent Transactions</div>
            <div className="text-sm text-gray-500">
              Here are today&apos; recent transaction
            </div>
          </div>
        )}

        <div className="flex flex-col flex-1">
          {hasSelectedBranch ? (
            <GenericTable
              isLoading={isListLoading}
              id="recent-transactions-table"
              withHover
              noRowsLabel={<EmptyState />}
              format={[
                {
                  key: (item) => <span>{item?.order_number || '-'}</span>,
                  label: 'ORDER NUMBER',
                  widthClassName: 'flex-1',
                },
                // {
                //   key: (item) => <span>{item?.table_number || '-'}</span>,
                //   label: 'TABLE NUMBER',
                //   widthClassName: 'flex-1',
                // },
                {
                  key: (item) => <span>{item?.subtotal_human || '-'}</span>,
                  label: 'TOTAL',
                  widthClassName: 'flex-1',
                },
                {
                  key: (item) => (
                    <div className="flex items-center gap-1">
                      <Button
                        label={
                          <>
                            <span className="md:hidden">View</span>
                            <span className="hidden md:block">View Order</span>
                          </>
                        }
                        className="btn font-semibold xs !text-sm !text-left"
                        onClick={handleOnView(item)}
                      />
                    </div>
                  ),
                  label: '',
                  widthClassName: 'w-32 md:w-48',
                },
              ]}
              data={list}
            />
          ) : (
            <div className="m-auto">
              <div className="ml-[-7px] -mt-2">
                <EmptyState
                  contentClassName="!space-y-0"
                  containerClassName="!bg-transparent"
                  {...(!hasSelectedBranch && {
                    label: 'No branch selected',
                    note: 'To proceed, select a branch first.',
                  })}
                />
              </div>
            </div>
          )}
        </div>

        <Pagination
          value={pagination}
          small
          onChange={handleOnChangePage}
          isLoading={isListLoading}
        />
      </div>
    </div>
  );
}

RecentTransactions.defaultProps = {
  canProceed:
    (event) =>
    (...args) =>
      typeof event === 'function' ? event(args) : event,
  branch: {},
};

RecentTransactions.propTypes = {
  canProceed: PropTypes.instanceOf(Function),
  branch: PropTypes.instanceOf(Object),
};

export default RecentTransactions;
